import { Event, MediaAspectRatio } from "@markit/common.types";
import { useTheme } from "../../../hooks/useTheme";
import { Colors } from "../../../utils/colors";
import { Icon } from "@iconify/react";
import { useCallback, useMemo, useState } from "react";
import AlertContainer from "../../Containers/AlertContainer";
import {
  errorAlertText,
  errorSubAlertText,
  MEDIA_UPLOAD_CODES,
  getImageAspectRatio,
  uploadMedia,
} from "../../../utils/photoUtils";
import {
  getCanonicalMediaUrls,
  isEventRelatedType,
} from "@markit/common.utils";
import { FileUploader } from "react-drag-drop-files";
import { MAX_MULTI_IMAGES } from "../../../utils/mediaUtils";

interface CreateImageModalProps {
  eventSettings: Event;
  updateEventSettings: (update: Partial<Event>) => void;
  setIsImageModalOpen: (isImageModalOpen: boolean) => void;
  selectedImage?: string; // when editing an existing image
  setSelectedImage?: (image: string) => void;
  sampleEventPhotos: string[];
  multipleMedia?: boolean;
}

const CreateImageModal = (props: CreateImageModalProps) => {
  const {
    eventSettings,
    updateEventSettings,
    setIsImageModalOpen,
    selectedImage,
    setSelectedImage,
    sampleEventPhotos,
    multipleMedia,
  } = props;
  const { theme } = useTheme(
    isEventRelatedType(eventSettings.eventType)
      ? eventSettings.theme
      : undefined
  );
  const [alertText, setAlertText] = useState("");
  const [alertSubText, setAlertSubText] = useState("");
  const [uploadLoading, setUploadLoading] = useState(false);

  const existingMediaUrls = useMemo(
    () => getCanonicalMediaUrls(eventSettings),
    [eventSettings]
  );

  /*  image selecting functions */
  const updateEventMediaUrls = useCallback(
    async (newMediaUrls: string[]) => {
      // Update aspect ratio when setting/replacing the only image; keep it when adding more.
      const isAddingAdditionalMedia =
        Boolean(multipleMedia) &&
        !selectedImage &&
        existingMediaUrls.length > 0;
      const isChangingOnlyExistingMedia =
        existingMediaUrls.length === 0 ||
        !multipleMedia ||
        (Boolean(multipleMedia) &&
          Boolean(selectedImage) &&
          existingMediaUrls.length === 1);
      const shouldUpdateAspectRatio =
        !isAddingAdditionalMedia && isChangingOnlyExistingMedia;
      const mediaAspectRatio = shouldUpdateAspectRatio
        ? (await getImageAspectRatio(newMediaUrls[0])) || MediaAspectRatio._1_1
        : eventSettings.mediaAspectRatio;

      if (multipleMedia) {
        if (selectedImage) {
          // Should replace the selected image with the new uploaded image starting at the index of the selected image
          const finalMediaUrls = [...existingMediaUrls];
          const selectedIndex = finalMediaUrls.indexOf(selectedImage);
          if (selectedIndex === -1) {
            return;
          }
          finalMediaUrls.splice(selectedIndex, 1, ...newMediaUrls);
          updateEventSettings({ mediaURLs: finalMediaUrls, mediaAspectRatio });
        } else {
          // Append new image(s) to the end of the existing media URLs
          const finalMediaUrls = [...existingMediaUrls, ...newMediaUrls];
          updateEventSettings({ mediaURLs: finalMediaUrls, mediaAspectRatio });
        }
      } else {
        // Single image support: replace the existing media URL with the new uploaded image
        updateEventSettings({ mediaURLs: newMediaUrls, mediaAspectRatio });
      }
      setIsImageModalOpen(false);
      setSelectedImage?.("");
    },
    [
      eventSettings.mediaAspectRatio,
      existingMediaUrls,
      multipleMedia,
      selectedImage,
      setIsImageModalOpen,
      setSelectedImage,
      updateEventSettings,
    ]
  );

  const atImageLimit = useMemo(
    () => existingMediaUrls.length >= MAX_MULTI_IMAGES,
    [existingMediaUrls]
  );

  const uploadImage = useCallback(
    (input: File | File[] | FileList) => {
      let files: File[] = [];
      if (input instanceof File) {
        files = [input];
      } else if (Array.isArray(input)) {
        files = input;
      } else if (input instanceof FileList) {
        files = Array.from(input);
      } else {
        return Promise.resolve();
      }

      if (files.length === 0) {
        return Promise.resolve();
      }

      const countAfterUpload = selectedImage
        ? existingMediaUrls.length - 1 + files.length
        : existingMediaUrls.length + files.length;
      if (countAfterUpload > MAX_MULTI_IMAGES) {
        setAlertText("Max number of images reached");
        setAlertSubText(
          `You can only upload up to ${MAX_MULTI_IMAGES} images. Remove some before adding more.`
        );
        return Promise.resolve();
      }

      return new Promise<void>((resolve, reject) => {
        (async () => {
          setUploadLoading(true);
          const uploadedUrls: string[] = [];

          for (const file of files) {
            const uploadData = await uploadMedia(file, false);
            if (uploadData.uploadCode !== MEDIA_UPLOAD_CODES.SUCCESS) {
              setAlertText(errorAlertText(uploadData.uploadCode));
              setAlertSubText(errorSubAlertText(uploadData.uploadCode));
              setUploadLoading(false);
              resolve();
              return;
            }
            uploadedUrls.push(uploadData.blobUrl);
          }

          updateEventMediaUrls(uploadedUrls);
          resolve();
          setUploadLoading(false);
        })().catch((error) => {
          setUploadLoading(false);
          reject(error);
        });
      });
    },
    [existingMediaUrls, selectedImage, updateEventMediaUrls]
  );

  const selectSampleImage = useCallback(
    async (index: number) => {
      if (atImageLimit && !selectedImage) {
        setAlertText("Maximum 12 images");
        setAlertSubText(
          `You can only have up to ${MAX_MULTI_IMAGES} images. Remove some before adding more.`
        );
        return;
      }

      updateEventMediaUrls([sampleEventPhotos[index]]);
    },
    [atImageLimit, sampleEventPhotos, selectedImage, updateEventMediaUrls]
  );
  /*  END image selecting functions */

  return (
    <>
      <div className="modalBackground">
        <div
          className="modalContent"
          style={{ width: "50vw", padding: 0, ...theme.TertiaryBG }}
        >
          <div
            className="AlignedRow"
            style={{
              boxSizing: "border-box",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              padding: "14px",
            }}
          >
            <div>
              <h4 className="AboutSubtitle" style={theme.PrimaryText}>
                Choose Photo
              </h4>
              <h4
                style={{
                  fontWeight: "400",
                  color: Colors.GRAY2,
                  fontSize: 14,
                  marginTop: 2,
                }}
              >
                Upload your own photo or choose from below
              </h4>
            </div>
            <Icon
              icon={"ion:close-circle"}
              height={24}
              onClick={() => setIsImageModalOpen(false)}
              style={{ color: Colors.GRAY1, cursor: "pointer" }}
            />
          </div>
          <div
            style={{
              ...theme.SecondaryBG,
              width: "100%",
              boxSizing: "border-box",
              padding: "14px",
              borderRadius: "0 0 8px 8px",
            }}
          >
            <FileUploader
              handleChange={uploadImage}
              name="file"
              types={["JPG", "JPEG", "PNG"]}
              multiple={multipleMedia}
              onTypeError={() => {
                setAlertText("Invalid File Type");
                setAlertSubText(
                  "Sorry, the uploaded file type is not supported. Please upload a .jpg, .jpeg, or .png file."
                );
              }}
              dropMessageStyle={{ opacity: 0.5 }}
              disabled={uploadLoading}
            >
              <div
                style={{
                  border: `2px dashed ${theme.DividerColor.borderColor}`,
                  display: "flex",
                  flexDirection: "column",
                  padding: "14px",
                  gap: "2px",
                  borderRadius: "8px",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <Icon
                  icon={"mdi:image-size-select-actual"}
                  height={24}
                  style={{ color: Colors.GRAY2 }}
                />
                <h4 className="AboutSubtitle" style={theme.PrimaryText}>
                  Click or drag here to upload
                </h4>
                <h4
                  style={{
                    fontWeight: "400",
                    color: Colors.GRAY2,
                    fontSize: 14,
                    marginTop: 2,
                  }}
                >
                  Or choose an image below. Images keep their original aspect
                  ratio.
                </h4>
              </div>
            </FileUploader>
            <h4
              className="AboutSubtitle"
              style={{ paddingBlock: 14, ...theme.PrimaryText }}
            >
              Photos
            </h4>
            <div className="photoGallerySample">
              {sampleEventPhotos.map((image, index) => {
                return (
                  <img
                    alt="SampleImages"
                    src={image}
                    key={index}
                    style={{ cursor: "pointer" }}
                    onClick={() => selectSampleImage(index)}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <AlertContainer
        headerComp={
          <div className="ColumnNormal" style={{ gap: 4 }}>
            <Icon icon="mdi-image-remove" height={40} />
            <span style={{ fontSize: 16, fontWeight: "500" }}>{alertText}</span>
          </div>
        }
        subHeaderComp={alertSubText}
        theme={theme}
        closeModal={() => setAlertText("")}
        hideModal={alertText === ""}
      />
    </>
  );
};

export default CreateImageModal;
